import {
  Asset,
  Onboarding,
  OnboardingPage,
  OnboardingQuestion,
  OnboardingQuestionType,
  QuestionOption,
  QuestionValidation,
} from '../generated/gql-types';

export const WORK_ELIGIBILITY_PAGE_KEY = 'workEligibility';
export const WORK_QUALIFICATIONS_PAGE_KEY = 'workQualifications';

/** Child question types we render under a document upload on web. */
const SUPPORTED_CHILD_TYPES = new Set([
  OnboardingQuestionType.Date,
  OnboardingQuestionType.TextInput,
  OnboardingQuestionType.Email,
  OnboardingQuestionType.MultiLineTextInput,
  OnboardingQuestionType.Dropdown,
  OnboardingQuestionType.SingleSelect,
  OnboardingQuestionType.Radio,
]);

export type DocumentChildQuestion = {
  key: string;
  title: string;
  type: OnboardingQuestionType;
  required: boolean;
  placeholder?: string | null;
  validations?: QuestionValidation[] | null;
  options?: QuestionOption[] | null;
};

export type DocumentSlot = {
  tag: string;
  title: string;
  required: boolean;
  childQuestions: DocumentChildQuestion[];
};

const FILE_QUESTION_TYPES = new Set([
  OnboardingQuestionType.FileUpload,
  OnboardingQuestionType.PhotoUpload,
]);

function extractChildQuestions(
  question: OnboardingQuestion,
): DocumentChildQuestion[] {
  const children: DocumentChildQuestion[] = [];

  for (const child of question.childQuestions ?? []) {
    if (!child.key) {
      continue;
    }
    if (!SUPPORTED_CHILD_TYPES.has(child.type)) {
      continue;
    }
    children.push({
      key: child.key,
      title: child.title,
      type: child.type,
      required: child.required,
      placeholder: child.placeholder,
      validations: child.validations,
      options: child.options,
    });
  }

  return children;
}

function questionToSlot(question: OnboardingQuestion): DocumentSlot | null {
  if (!FILE_QUESTION_TYPES.has(question.type)) {
    return null;
  }

  return {
    tag: question.key,
    title: question.title,
    required: question.required,
    childQuestions: extractChildQuestions(question),
  };
}

function collectSlotsFromQuestions(
  questions: OnboardingQuestion[] | null | undefined,
): DocumentSlot[] {
  const slots: DocumentSlot[] = [];
  const seen = new Set<string>();

  const walk = (question: OnboardingQuestion) => {
    const slot = questionToSlot(question);
    if (slot && !seen.has(slot.tag)) {
      seen.add(slot.tag);
      slots.push(slot);
    }
    question.childQuestions?.forEach(walk);
  };

  questions?.forEach(walk);
  return slots;
}

function getPageSlots(
  onboarding: Onboarding | null | undefined,
  pageKey: string,
): DocumentSlot[] {
  const page = onboarding?.onboardingJSON?.pages?.find(
    (p: OnboardingPage) => p.key === pageKey,
  );
  return collectSlotsFromQuestions(page?.questions ?? undefined);
}

export function getWorkEligibilityDocumentSlots(
  onboarding: Onboarding | null | undefined,
): DocumentSlot[] {
  return getPageSlots(onboarding, WORK_ELIGIBILITY_PAGE_KEY);
}

export function getWorkQualificationDocumentSlots(
  onboarding: Onboarding | null | undefined,
): DocumentSlot[] {
  return getPageSlots(onboarding, WORK_QUALIFICATIONS_PAGE_KEY);
}

export function getAllRequiredDocumentTags(
  eligibilitySlots: DocumentSlot[],
  qualificationSlots: DocumentSlot[],
): Set<string> {
  return new Set(
    [...eligibilitySlots, ...qualificationSlots].map((slot) => slot.tag),
  );
}

export function getOrphanAssets(
  assets: Asset[],
  requiredTags: Set<string>,
): Asset[] {
  return assets.filter((asset) => asset.tag && !requiredTags.has(asset.tag));
}
